import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Home, Users, MapPin, Wallet, Sparkles, ChevronRight, Plus, Calendar, Store, Trash2, X } from "lucide-react";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/AuthContext";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";

const STORE_ICON = "🛒";
const MODE_LABEL = { smart: "⚡ Smart", relaxed: "💰 Romslig" };

/** Home Edition Admin Dashboard
 *  Dedikert side for Home Edition-administrator (brukernavn "Admin" / passord "home3344").
 *  Bruker store ikoner, klar språk og én-klikk handlinger — egnet for brukere i alle aldre.
 */
export default function HomeAdmin() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [overview, setOverview] = useState(null);
  const [loading, setLoading] = useState(true);
  const [createOpen, setCreateOpen] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [creating, setCreating] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [form, setForm] = useState({
    name: "",
    address: "",
    household_days: 7,
    num_onboard: 4,
    monthly_budget_nok: 0,
    preferred_store: "rema",
    budget_mode: "relaxed",
  });

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await api.getAdminOverview();
        if (!cancelled) setOverview(res.data);
      } catch (err) {
        toast.error(err.response?.data?.detail || "Kunne ikke laste oversikt");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const openHousehold = async (boatId) => {
    try {
      await api.activateBoat(boatId);
      toast.success("Åpner husstand...");
      setTimeout(() => navigate("/home"), 400);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Kunne ikke åpne");
    }
  };

  const copyCode = async (text) => {
    try {
      await navigator.clipboard.writeText(text);
      toast.success("Kopiert! Del koden med familien.");
    } catch {
      toast.error("Kunne ikke kopiere");
    }
  };

  const reloadOverview = async () => {
    try {
      const res = await api.getAdminOverview();
      setOverview(res.data);
    } catch { /* ignore */ }
  };

  const handleCreate = async () => {
    if (!form.name.trim()) { toast.error("Navn er påkrevd"); return; }
    setCreating(true);
    try {
      await api.createHousehold({
        ...form,
        household_days: parseInt(form.household_days) || 7,
        num_onboard: parseInt(form.num_onboard) || 4,
        monthly_budget_nok: parseFloat(form.monthly_budget_nok) || 0,
      });
      toast.success(`Husstand "${form.name}" opprettet`);
      setCreateOpen(false);
      setForm({ name: "", address: "", household_days: 7, num_onboard: 4, monthly_budget_nok: 0, preferred_store: "rema", budget_mode: "relaxed" });
      reloadOverview();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Kunne ikke opprette");
    } finally { setCreating(false); }
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      const res = await api.deleteHousehold(deleteTarget.id);
      toast.success(`Husstand "${res.data.name}" slettet`);
      setDeleteTarget(null);
      reloadOverview();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Kunne ikke slette");
    } finally { setDeleting(false); }
  };

  if (user?.role !== "admin") {
    return <div className="p-6 text-center text-sm text-stone-500">Kun admin har tilgang til denne siden.</div>;
  }
  if (loading) return <div className="p-6 text-center text-sm text-stone-400">Laster...</div>;
  if (!overview) return null;

  const households = overview.households || [];

  return (
    <div className="p-4 sm:p-6 max-w-4xl mx-auto" data-testid="home-admin-page">
      {/* Hero */}
      <div className="mb-6 bg-gradient-to-br from-amber-500 via-orange-500 to-red-500 rounded-2xl p-6 sm:p-8 text-white shadow-lg">
        <div className="flex items-center gap-3 mb-2">
          <Home className="h-7 w-7" />
          <span className="text-xs uppercase tracking-[0.3em] font-bold text-white/90">Home Edition · Admin</span>
        </div>
        <h1 className="text-4xl sm:text-5xl font-semibold tracking-tight" style={{ fontFamily: "'Cormorant Garamond', serif" }}>
          Velkommen, {user?.name || "Admin"}
        </h1>
        <p className="text-sm text-white/85 mt-2">Her styrer du husstander og familiemedlemmer. Klikk en husstand for å åpne den, eller legg til nye medlemmer manuelt.</p>
      </div>

      {/* Big stats — senior-friendly */}
      <div className="grid grid-cols-2 gap-3 mb-6">
        <div className="bg-white border border-stone-100 rounded-2xl p-5 text-center" data-testid="stat-households">
          <div className="w-12 h-12 mx-auto mb-2 rounded-2xl bg-amber-50 flex items-center justify-center">
            <Home className="h-6 w-6 text-amber-600" />
          </div>
          <div className="text-4xl font-bold text-stone-800" style={{ fontFamily: "'Cormorant Garamond', serif" }}>{households.length}</div>
          <div className="text-xs text-stone-500 font-semibold">Husstander</div>
        </div>
        <div className="bg-white border border-stone-100 rounded-2xl p-5 text-center" data-testid="stat-members">
          <div className="w-12 h-12 mx-auto mb-2 rounded-2xl bg-emerald-50 flex items-center justify-center">
            <Users className="h-6 w-6 text-emerald-600" />
          </div>
          <div className="text-4xl font-bold text-stone-800" style={{ fontFamily: "'Cormorant Garamond', serif" }}>
            {households.reduce((a, h) => a + (h.member_count || 0), 0)}
          </div>
          <div className="text-xs text-stone-500 font-semibold">Familiemedlemmer</div>
        </div>
      </div>

      {/* Create household CTA */}
      <button
        onClick={() => setCreateOpen(true)}
        className="w-full mb-4 flex items-center gap-3 bg-white border-2 border-dashed border-stone-200 hover:border-amber-400 rounded-2xl p-4 text-left transition-colors"
        data-testid="new-household-btn"
      >
        <div className="w-14 h-14 rounded-2xl bg-amber-50 flex items-center justify-center flex-shrink-0">
          <Plus className="h-7 w-7 text-amber-600" />
        </div>
        <div className="flex-1">
          <div className="text-lg font-semibold text-stone-800" style={{ fontFamily: "'Cormorant Garamond', serif" }}>Ny husstand</div>
          <div className="text-sm text-stone-500">Opprett og legg til familiemedlemmer</div>
        </div>
        <ChevronRight className="h-5 w-5 text-stone-400" />
      </button>

      {/* Household list — big, tap-friendly */}
      <h2 className="text-2xl font-semibold text-stone-800 mb-3" style={{ fontFamily: "'Cormorant Garamond', serif" }}>Dine husstander</h2>
      {households.length === 0 ? (
        <div className="bg-white border border-stone-100 rounded-2xl p-6 text-center text-stone-400">Ingen husstander ennå.</div>
      ) : (
        <div className="space-y-3" data-testid="household-list">
          {households.map((h) => (
            <div key={h.id} className="bg-white border border-stone-100 rounded-2xl overflow-hidden" data-testid={`home-admin-household-${h.id}`}>
              <div className="flex items-stretch">
                <button
                  onClick={() => openHousehold(h.id)}
                  className="flex-1 flex items-center gap-4 p-5 text-left hover:bg-stone-50 transition-colors"
                  data-testid={`open-household-${h.id}`}
                >
                  <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-amber-100 to-orange-100 flex items-center justify-center flex-shrink-0">
                    <Home className="h-8 w-8 text-amber-700" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="text-xl font-semibold text-stone-800 truncate" style={{ fontFamily: "'Cormorant Garamond', serif" }}>{h.name}</div>
                    <div className="text-sm text-stone-500 truncate">Eier: {h.owner_name}</div>
                    {h.address && (
                      <div className="text-xs text-stone-400 mt-0.5 flex items-center gap-1"><MapPin className="h-3 w-3" /> {h.address}</div>
                    )}
                  </div>
                  <ChevronRight className="h-6 w-6 text-stone-300 flex-shrink-0" />
                </button>
                <button
                  onClick={() => setDeleteTarget(h)}
                  className="px-4 border-l border-stone-50 text-stone-300 hover:text-red-600 hover:bg-red-50 transition-colors"
                  data-testid={`delete-household-${h.id}`}
                  aria-label="Slett husstand"
                  title="Slett husstand"
                >
                  <Trash2 className="h-5 w-5" />
                </button>
              </div>
              <div className="border-t border-stone-50 px-5 py-3 bg-stone-50/50">
                <div className="grid grid-cols-2 gap-3 text-sm">
                  <div className="flex items-center gap-2">
                    <Users className="h-4 w-4 text-emerald-600" />
                    <span className="text-stone-700"><strong>{h.member_count}</strong> medlemmer</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Calendar className="h-4 w-4 text-amber-600" />
                    <span className="text-stone-700">Planlegger <strong>{h.household_days || 7}</strong> dager</span>
                  </div>
                  {h.monthly_budget_nok > 0 && (
                    <div className="flex items-center gap-2">
                      <Wallet className="h-4 w-4 text-emerald-600" />
                      <span className="text-stone-700"><strong>{h.monthly_budget_nok} kr</strong>/mnd {MODE_LABEL[h.budget_mode] || ""}</span>
                    </div>
                  )}
                  <div className="flex items-center gap-2">
                    <Store className="h-4 w-4 text-stone-500" />
                    <span className="text-stone-700">{STORE_ICON} {h.preferred_store}</span>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Help */}
      <div className="mt-8 p-5 rounded-2xl bg-sky-50 border border-sky-100" data-testid="admin-help">
        <h3 className="text-base font-semibold text-sky-900 mb-2 flex items-center gap-2" style={{ fontFamily: "'Cormorant Garamond', serif" }}>
          <Sparkles className="h-4 w-4" /> Hvordan legger jeg til familiemedlemmer?
        </h3>
        <ol className="space-y-1.5 text-sm text-sky-900/80 list-decimal list-inside">
          <li>Klikk på husstanden du vil legge til medlemmer i</li>
          <li>Gå til <strong>Husstand</strong> i menyen til venstre</li>
          <li>Klikk <strong>Ny bruker</strong> og opprett brukernavn + passord</li>
          <li>Del brukernavn og passord med familiemedlemmet så de kan logge inn</li>
        </ol>
      </div>
      {/* Create household dialog */}
      <Dialog open={createOpen} onOpenChange={setCreateOpen}>
        <DialogContent className="max-w-md" data-testid="create-household-dialog">
          <DialogHeader>
            <DialogTitle style={{ fontFamily: "'Cormorant Garamond', serif" }}>Ny husstand</DialogTitle>
            <DialogDescription>Opprett en ny husstand. Du blir automatisk admin og medlem.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <label className="text-xs text-stone-500 font-semibold mb-1 block">Navn *</label>
              <input
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                placeholder="f.eks. Familien Nordmann"
                className="w-full rounded-md border border-stone-200 bg-white px-3 py-2 text-sm outline-none focus:border-amber-500"
                data-testid="form-name"
              />
            </div>
            <div>
              <label className="text-xs text-stone-500 font-semibold mb-1 block">Adresse</label>
              <input
                value={form.address}
                onChange={(e) => setForm({ ...form, address: e.target.value })}
                placeholder="Valgfri"
                className="w-full rounded-md border border-stone-200 bg-white px-3 py-2 text-sm outline-none focus:border-amber-500"
                data-testid="form-address"
              />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-xs text-stone-500 font-semibold mb-1 block">Antall personer</label>
                <input
                  type="number" min="1" max="20"
                  value={form.num_onboard}
                  onChange={(e) => setForm({ ...form, num_onboard: e.target.value })}
                  className="w-full rounded-md border border-stone-200 bg-white px-3 py-2 text-sm outline-none focus:border-amber-500"
                  data-testid="form-num-onboard"
                />
              </div>
              <div>
                <label className="text-xs text-stone-500 font-semibold mb-1 block">Planlegg antall dager</label>
                <input
                  type="number" min="1" max="14"
                  value={form.household_days}
                  onChange={(e) => setForm({ ...form, household_days: e.target.value })}
                  className="w-full rounded-md border border-stone-200 bg-white px-3 py-2 text-sm outline-none focus:border-amber-500"
                  data-testid="form-days"
                />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-xs text-stone-500 font-semibold mb-1 block">Månedlig budsjett (NOK)</label>
                <input
                  type="number" min="0"
                  value={form.monthly_budget_nok}
                  onChange={(e) => setForm({ ...form, monthly_budget_nok: e.target.value })}
                  className="w-full rounded-md border border-stone-200 bg-white px-3 py-2 text-sm outline-none focus:border-amber-500"
                  data-testid="form-budget"
                />
              </div>
              <div>
                <label className="text-xs text-stone-500 font-semibold mb-1 block">Foretrukket butikk</label>
                <select
                  value={form.preferred_store}
                  onChange={(e) => setForm({ ...form, preferred_store: e.target.value })}
                  className="w-full rounded-md border border-stone-200 bg-white px-3 py-2 text-sm outline-none focus:border-amber-500"
                  data-testid="form-store"
                >
                  <option value="rema">Rema 1000</option>
                  <option value="kiwi">Kiwi</option>
                  <option value="extra">Extra</option>
                  <option value="coop">Coop</option>
                  <option value="meny">Meny</option>
                </select>
              </div>
            </div>
          </div>
          <DialogFooter>
            <button
              onClick={() => setCreateOpen(false)}
              className="rounded-md border border-stone-200 text-stone-600 px-3 py-2 text-sm font-semibold hover:bg-stone-50"
              data-testid="cancel-create-btn"
            >Avbryt</button>
            <button
              onClick={handleCreate}
              disabled={creating || !form.name.trim()}
              className="rounded-md bg-amber-600 text-white px-4 py-2 text-sm font-semibold hover:bg-amber-700 disabled:opacity-50"
              data-testid="submit-create-btn"
            >
              {creating ? "Oppretter..." : "Opprett husstand"}
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete confirmation dialog */}
      <Dialog open={!!deleteTarget} onOpenChange={(v) => !v && setDeleteTarget(null)}>
        <DialogContent className="max-w-md" data-testid="delete-household-dialog">
          <DialogHeader>
            <DialogTitle style={{ fontFamily: "'Cormorant Garamond', serif" }}>Slett husstand</DialogTitle>
            <DialogDescription>
              Vil du virkelig slette <strong>{deleteTarget?.name}</strong>? Alle beholdninger, handlelister, kvitteringer og matplan for denne husstanden slettes permanent.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <button
              onClick={() => setDeleteTarget(null)}
              className="rounded-md border border-stone-200 text-stone-600 px-3 py-2 text-sm font-semibold hover:bg-stone-50"
              data-testid="cancel-delete-btn"
            >Avbryt</button>
            <button
              onClick={handleDelete}
              disabled={deleting}
              className="rounded-md bg-red-600 text-white px-4 py-2 text-sm font-semibold hover:bg-red-700 disabled:opacity-50 inline-flex items-center gap-1.5"
              data-testid="confirm-delete-btn"
            >
              <Trash2 className="h-4 w-4" /> {deleting ? "Sletter..." : "Ja, slett"}
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
